import { apiSlice } from "@/features/api/ApiSlice";
import {
  WorkflowResponse,
  WorkflowCreate,
  WorkflowUpdate,
  WorkflowNodeType,
  WorkflowInstanceResponse,
  WorkflowSuggestion,
  WorkflowSuggestionsResponse,
  WorkflowApprovalListResponse,
} from "@/app/types/workflow";

export const workFlowBuilderApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTriggerTypes: builder.query<any, void>({
      query: () => ({
        url: "/event-workflow/trigger-types",
        method: "GET",
      }),
    }),
    getEventTypes: builder.query<any, void>({
      query: () => ({
        url: "/event-workflow/event-types",
        method: "GET",
      }),
    }),
    getScheduleTypes: builder.query<any, void>({
      query: () => ({
        url: "/event-workflow/schedule-types",
        method: "GET",
      }),
    }),
    getNodeTypes: builder.query<any, void>({
      query: () => ({
        url: "/event-workflow/node-types",
        method: "GET",
      }),
    }),
    getActionTypes: builder.query<any, void>({
      query: () => ({
        url: "/event-workflow/action-types",
        method: "GET",
      }),
    }),
    getConditionalOperators: builder.query<any, void>({
      query: () => ({
        url: "/event-workflow/conditional-operators",
        method: "GET",
      }),
    }),
    getTriggers: builder.query<WorkflowNodeType[], void>({
      query: () => ({
        url: "/event-workflow/triggers",
        method: "GET",
      }),
    }),
    getActions: builder.query<WorkflowNodeType[], void>({
      query: () => ({
        url: "/event-workflow/actions",
        method: "GET",
      }),
    }),
    getConditions: builder.query<WorkflowNodeType[], void>({
      query: () => ({
        url: "/event-workflow/conditions",
        method: "GET",
      }),
    }),
    getTriggerableWorkflows: builder.query<WorkflowNodeType[], void>({
      query: () => ({
        url: "/event-workflow/triggerable-workflows",
        method: "GET",
      }),
    }),
    createWorkflow: builder.mutation<WorkflowResponse, WorkflowCreate>({
      query: (body) => ({
        url: "/event-workflow/workflow",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WorkFlowBuilder"],
    }),
    getAllWorkflows: builder.query<
      WorkflowResponse[] | { data: WorkflowResponse[]; total_count: number; filtered_count: number },
      {
        offset?: number;
        limit?: number;
        search_key?: string;
        sort_key?: string;
        sort_order?: string;
        status?: string;
      }
    >({
      query: (params) => ({
        url: "/event-workflow/get-all-workflows",
        method: "GET",
        params,
      }),
      providesTags: ["WorkFlowBuilder"],
    }),
    getWorkflow: builder.query<WorkflowResponse, number>({
      query: (workflowId) => ({
        url: `/event-workflow/${workflowId}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "WorkFlowBuilder", id }],
    }),
    getWorkflowVersion: builder.query<WorkflowResponse, { workflowId: number, workflowVersionId?: number }>({
      query: ({ workflowId, workflowVersionId }) => ({
        url: `/event-workflow/workflow_version/${workflowId}`,
        method: "GET",
        params: { version: workflowVersionId || 0 },
      }),
      providesTags: (result, error, { workflowId, workflowVersionId }) => [{ type: "WorkFlowBuilder", id: `${workflowId}-${workflowVersionId}` }],
    }),
    updateWorkflow: builder.mutation<
      WorkflowResponse,
      { id: number; body: WorkflowUpdate }
    >({
      query: ({ id, body }) => ({
        url: `/event-workflow/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WorkFlowBuilder", id },
        "WorkFlowBuilder",
      ],
    }),
    deleteWorkflow: builder.mutation<void, number>({
      query: (workflowId) => ({
        url: `/event-workflow/${workflowId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WorkFlowBuilder"],
    }),
    duplicateWorkflow: builder.mutation<
      WorkflowResponse,
      { id: number; }
    >({
      query: ({ id }) => ({
        url: `/event-workflow/${id}/duplicate`,
        method: "POST",
      }),
      invalidatesTags: ["WorkFlowBuilder"],
    }),
    updateWorkflowStatus: builder.mutation<
      WorkflowResponse,
      { id: number; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/event-workflow/${id}/status`,
        method: "PATCH",
        params: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WorkFlowBuilder", id },
        "WorkFlowBuilder",
      ],
    }),
    getAllWorkflowExecutions: builder.query<
      any,
      {
        id: number | string;
        offset?: number;
        limit?: number;
        search_key?: string;
        sort_key?: string;
        sort_order?: string;
        status?: string;
      }
    >({
      query: ({ id, ...params }) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
        const queryString = queryParams.toString();
        return {
          url: `/event-workflow/${id}/executions${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ["WorkFlowBuilder"],
    }),
    getWorkflowExecutionDetail: builder.query<WorkflowInstanceResponse, number>({
      query: (instanceId) => ({
        url: `/event-workflow/execution/${instanceId}/detail`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "WorkFlowBuilder", id }],
    }),
    retryWorkflowInstance: builder.mutation<
      WorkflowInstanceResponse,
      number
    >({
      query: (instanceId) => ({
        url: `/event-workflow/execution/${instanceId}/retry`,
        method: "POST",
      }),
      invalidatesTags: ["WorkFlowBuilder"],
    }),
    pauseWorkflowInstance: builder.mutation<WorkflowInstanceResponse, number>({
      query: (instanceId) => ({
        url: `/event-workflow/execution/${instanceId}/pause`,
        method: "POST",
      }),
      invalidatesTags: ["WorkFlowBuilder"],
    }),
    resumeWorkflowInstance: builder.mutation<WorkflowInstanceResponse, number>({
      query: (instanceId) => ({
        url: `/event-workflow/execution/${instanceId}/resume`,
        method: "POST",
      }),
      invalidatesTags: ["WorkFlowBuilder"],
    }),
    cancelWorkflowInstance: builder.mutation<
      WorkflowInstanceResponse,
      number
    >({
      query: (instanceId) => ({
        url: `/event-workflow/execution/${instanceId}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["WorkFlowBuilder"],
    }),
    getWorkflowSuggestions: builder.mutation<WorkflowSuggestionsResponse, void>({
      query: () => ({
        url: "/event-workflow/suggestions",
        method: "GET",
        params: { key: "workflow_suggestion" },
      }),
    }),
    getPendingApprovals: builder.query<WorkflowApprovalListResponse, void>({
      query: () => ({
        url: "/event-workflow/approvals",
        method: "GET",
      }),
      providesTags: ["WorkFlowBuilder"],
    }),
    submitApprovalDecision: builder.mutation<
      { success: boolean; approved: boolean; instance_id: number; node_exec_id: number },
      { node_exec_id: number; approved: boolean; comments?: string }
    >({
      query: ({ node_exec_id, approved, comments }) => ({
        url: `/event-workflow/approvals/${node_exec_id}/decision`,
        method: "POST",
        body: { approved, comments },
      }),
      invalidatesTags: ["WorkFlowBuilder"],
    }),
  }),
});

export const {
  useGetTriggerTypesQuery,
  useGetEventTypesQuery,
  useGetScheduleTypesQuery,
  useGetNodeTypesQuery,
  useGetActionTypesQuery,
  useGetConditionalOperatorsQuery,
  useGetTriggersQuery,
  useGetActionsQuery,
  useGetConditionsQuery,
  useGetTriggerableWorkflowsQuery,
  useCreateWorkflowMutation,
  useGetAllWorkflowsQuery,
  useGetWorkflowQuery,
  useGetWorkflowVersionQuery,
  useUpdateWorkflowMutation,
  useDeleteWorkflowMutation,
  useDuplicateWorkflowMutation,
  useUpdateWorkflowStatusMutation,
  useGetAllWorkflowExecutionsQuery,
  useGetWorkflowExecutionDetailQuery,
  useRetryWorkflowInstanceMutation,
  usePauseWorkflowInstanceMutation,
  useResumeWorkflowInstanceMutation,
  useCancelWorkflowInstanceMutation,
  useGetWorkflowSuggestionsMutation,
  useGetPendingApprovalsQuery,
  useSubmitApprovalDecisionMutation,
} = workFlowBuilderApi;
